// src/components/GreetingCard/VideoCanvas.jsx

import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize } from "lucide-react";

/**
 * VideoCanvas component for rendering video greeting cards.
 * Supports video playback with controls, looping, and responsive sizing.
 */
const VideoCanvas = forwardRef(({
  videoSrc,
  cardWidth = 600,
  cardHeight = 400,
  autoPlay = true,
  loop = true,
  muted = true,
  showControls = true,
  isPreview = false,
  onVideoLoad = null,
  onVideoError = null,
  className = "",
  style = {},
}, ref) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(muted);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showControlsOverlay, setShowControlsOverlay] = useState(false);

  // Expose video control methods via ref
  useImperativeHandle(ref, () => ({
    play: () => videoRef.current?.play(),
    pause: () => videoRef.current?.pause(),
    restart: () => {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
    },
    getCurrentTime: () => videoRef.current?.currentTime || 0,
    getDuration: () => videoRef.current?.duration || 0,
    getVideoElement: () => videoRef.current,
  }));

  // Handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsLoaded(true);
      if (onVideoLoad) {
        onVideoLoad({
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
        });
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      if (!loop) setIsPlaying(false);
    };

    const handleError = (e) => {
      console.error("[VideoCanvas] Video error:", e);
      setHasError(true);
      if (onVideoError) {
        onVideoError(e);
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("error", handleError);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("error", handleError);
    };
  }, [loop, onVideoLoad, onVideoError]);

  // Auto-play on mount if enabled
  useEffect(() => {
    if (autoPlay && videoRef.current && isLoaded) {
      videoRef.current.play().catch((e) => {
        console.warn("[VideoCanvas] Autoplay blocked:", e);
        setIsPlaying(false);
      });
    }
  }, [autoPlay, isLoaded]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleSeek = (e) => {
    if (videoRef.current && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;
      videoRef.current.currentTime = percentage * duration;
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleFullscreen = () => {
    if (containerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        containerRef.current.requestFullscreen();
      }
    }
  };

  if (hasError) {
    return (
      <div
        ref={containerRef}
        className={`relative bg-gray-100 flex items-center justify-center ${className}`}
        style={{
          width: cardWidth,
          height: cardHeight,
          ...style,
        }}
      >
        <div className="text-center text-gray-500 p-4">
          <div className="text-4xl mb-2">🎬</div>
          <p className="text-sm">Failed to load video</p>
          <p className="text-xs mt-1">Please try again or use a different video</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={{
        width: cardWidth,
        height: cardHeight,
        ...style,
      }}
      onMouseEnter={() => showControls && setShowControlsOverlay(true)}
      onMouseLeave={() => showControls && setShowControlsOverlay(false)}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        src={videoSrc}
        muted={isMuted}
        loop={loop}
        playsInline
        preload="metadata"
        className="w-full h-full object-contain"
        style={{ backgroundColor: "#000" }}
      />

      {/* Loading indicator */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
        </div>
      )}

      {/* Play/Pause overlay button (center) - only show when controls are enabled */}
      {isLoaded && !isPreview && showControls && (
        <button
          onClick={togglePlay}
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
            showControlsOverlay || !isPlaying ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors">
            {isPlaying ? (
              <Pause className="w-8 h-8 text-white" />
            ) : (
              <Play className="w-8 h-8 text-white ml-1" />
            )}
          </div>
        </button>
      )}

      {/* Bottom controls bar */}
      {showControls && isLoaded && !isPreview && (
        <div
          className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 transition-opacity duration-200 ${
            showControlsOverlay ? "opacity-100" : "opacity-0"
          }`}
        >
          {/* Progress bar */}
          <div
            className="w-full h-1 bg-white/30 rounded-full cursor-pointer mb-2"
            onClick={handleSeek}
          >
            <div
              className="h-full bg-white rounded-full"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            />
          </div>

          {/* Controls row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={togglePlay}
                className="text-white hover:text-white/80 transition-colors"
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5" />
                )}
              </button>

              <button
                onClick={toggleMute}
                className="text-white hover:text-white/80 transition-colors"
              >
                {isMuted ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </button>

              <span className="text-white text-xs">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <button
              onClick={handleFullscreen}
              className="text-white hover:text-white/80 transition-colors"
            >
              <Maximize className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

    </div>
  );
});

VideoCanvas.displayName = "VideoCanvas";

export default VideoCanvas;
